import React from 'react';
import { MathJax } from 'better-react-mathjax';

export function SimplexIntro({ lite = false }) {
  const baseClass = lite
    ? 'space-y-3 text-justify hyphens-auto text-sm leading-relaxed text-white/90 md:text-base'
    : 'space-y-4 text-justify hyphens-auto leading-relaxed text-white/85 text-sm md:text-base';

  return (
    <div className={baseClass}>
      <p>
        The simplex algorithm is the classic method for linear programs (LPs).
        A linear program optimizes a linear objective subject to linear constraints and can be written in standard form as
      </p>

      <MathJax>{String.raw`
      \[
      \min \; c^T x \quad \text{subject to} \quad Ax = b,\; x \ge 0.
      \]
      `}</MathJax>

      <p>
        Here <MathJax inline>{'\(x\)'}</MathJax> is the vector of decision variables, <MathJax inline>{'\(c\)'}</MathJax> contains the 
        objective coefficients, <MathJax inline>{'\(A\)'}</MathJax> is the constraint
        matrix, and <MathJax inline>{'\(b\)'}</MathJax> is the right-hand side vector describing the limits of the constraints.
        Because both the objective and constraints are linear, the feasible set is a convex polyhedron.
        A central fact is that if an optimum exists, at least one optimal solution lies at a vertex of this polyhedron.
      </p>

      <p>
        Simplex exploits this structure by working only with these vertices.
        Algebraically, each vertex corresponds to a <strong>basis</strong>: 
        we select <MathJax inline>{'\(m\)'}</MathJax> independent columns of <MathJax inline>{'\(A\)'}</MathJax> that 
        form a matrix <MathJax inline>{'\(B\)'}</MathJax>. The associated variables
        (<em>basic variables</em>) are solved from  <MathJax inline>{String.raw `$B (x_B) = b $`}</MathJax>, while all remaining
        (<em>nonbasic variables</em>) are fixed to zero. This produces a <strong>basic feasible solution</strong>, i.e., exactly a corner of the polyhedron.
        Each step of simplex is a <em>pivot</em> that swaps one nonbasic variable into the basis and one basic variable out, moving to a neighboring vertex while preserving feasibility.
      </p>

      <p>
        If an obvious feasible starting basis is not available, the method uses two phases. <strong>Phase I</strong> introduces temporary artificial 
        variables and minimizes their sum to find any feasible solution of <MathJax inline>{'\(Ax=b\)'}</MathJax>. If this minimum is 
        zero, feasibility has been found. <strong>Phase II</strong> then removes the artificial variables, 
        restores the original objective <MathJax inline>{'\(c^T x\)'}</MathJax>, and continues pivoting to improve the objective until no further improvement is possible.
      </p>

    </div>
  );
}


export default function SimplexLongExplanation() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-white/80 text-justify hyphens-auto md:text-base">

      <h2 className="text-sm font-semibold md:text-base">1. What is a linear optimization problem?</h2>
      <p>
        A <strong>linear optimization problem</strong> (also called a <strong>linear program</strong>, LP)
        asks you to minimize or maximize a linear objective function under linear constraints.
        In compact notation we write
      </p>
      <MathJax>{String.raw`
        \[
        \text{min } c^T x
        \quad \text{subject to} \quad
        Ax \; (\le, =, \ge) \; b.
        \]
      `}</MathJax>
      <p>
        The solution vector <MathJax inline>{'\(x\)'}</MathJax> contains the unknowns, the vector
        <MathJax inline>{'\(c\)'}</MathJax> defines the objective, and <MathJax inline>{'\(A, b\)'}</MathJax> define the constraints. Unlike nonlinear optimization, 
        everything here is affine/linear, which has
        two major consequences:
      </p>
      <p>
        Linear programs are often written with inequalities <MathJax inline>{String.raw `$Ax \le b $`}</MathJax>.
        For the simplex method, these are converted to equalities by adding slack variables,
        leading to the standard simplex form <MathJax inline>{String.raw `$Ax = b, \, x \ge 0 $`}</MathJax>.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          The feasible set is <strong>convex</strong> (any line segment between feasible points stays feasible).
        </li>
        <li>
          The feasible set has a “polyhedral” geometry: in low dimensions it is a polygon/polytope with flat faces and corners.
        </li>
      </ul>
      <p>
        This geometry is the reason simplex works so well: if an optimal solution exists, then at least one
        optimum occurs at a <strong>corner (vertex)</strong> of the feasible polytope.
      </p>


      <h2 className="text-sm font-semibold md:text-base">2. Standard form: equalities and nonnegativity</h2>
      <p>
        Simplex is easiest to describe for the standard form
      </p>
      <MathJax>{String.raw`
        \[
        \min c^T x \quad \text{subject to} \quad Ax = b,\; x \ge 0.
        \]
      `}</MathJax>
      <p>
        Most LPs are not given exactly like this. So we transform them:
      </p>

      <h3 className="text-sm font-semibold md:text-base">2.1 Inequalities → equalities (slack/surplus variables)</h3>
      <p>
        Constraints of the type <MathJax inline>{String.raw `$a^T x \le b $`}</MathJax>. become equalities by adding a
        <strong>slack variable</strong> <MathJax inline>{'\(s \ge 0\)'}</MathJax>:
      </p>
      <MathJax>{String.raw`
        \[
        a^T x \le b \quad \Longleftrightarrow \quad a^T x + s = b,\; s \ge 0.
        \]
      `}</MathJax>
      <p>
        For <MathJax inline>{'\(\ge\)'}</MathJax> constraints we subtract a nonnegative <strong>surplus</strong>
        variable. Equalities already match the target form.
      </p>

      <h3 className="text-sm font-semibold md:text-base">2.2 Free variables → difference of nonnegative variables</h3>
      <p>
        If a variable is not constrained to be nonnegative, we can represent it as 
        a difference: <MathJax inline>{String.raw `$x = x^+ - x^-$`}</MathJax> with <MathJax inline>{String.raw `$x^+, x^- \ge 0$`}</MathJax>.
        This keeps the model linear and moves everything into the “all variables ≥ 0” setting.
      </p>

      <p>
        After these steps, the LP fits the simplex framework and we can reason in terms of feasible corners.
      </p>


      <h2 className="text-sm font-semibold md:text-base">3. Geometry: vertices, edges, and why simplex looks only at corners</h2>
      <p>
        In two dimensions, the feasible region is typically a polygon. In higher dimensions it is a polytope.
        Level sets of a linear objective are parallel hyperplanes. Minimizing <MathJax inline>{String.raw `$c^T x$`}</MathJax> means shifting such a 
        hyperplane “downwards” until it last touches the feasible polytope.
      </p>
      <p>
        That last touching point is always a <strong>vertex</strong> (or an entire face/edge in non-unique cases).
        So instead of searching continuously, simplex enumerates corners intelligently: it moves along edges to neighboring vertices.
      </p>


      <h2 className="text-sm font-semibold md:text-base">4. Algebraic view: bases and basic feasible solutions (BFS)</h2>
      <p>
        The simplex method is easiest to understand through the concept of a <strong>basis</strong>.
        Assume the LP is in standard form with <MathJax inline>{'\(m\)'}</MathJax> equality constraints 
        and <MathJax inline>{'\(n\)'}</MathJax> variables (typically <MathJax inline>{'m < n'}</MathJax>).
      </p>
      <p>
        Choose a set <MathJax inline>{String.raw `$ B$`}</MathJax> of exactly <MathJax inline>{'\(m\)'}</MathJax> variable indices
        such that the corresponding columns of <MathJax inline>{String.raw `$ A$`}</MathJax> form an invertible matrix
       <MathJax inline>{String.raw `$ B$`}</MathJax> (called the <em>basis matrix</em>). The remaining indices form the
        <em>nonbasic</em> set <MathJax inline>{'N'}</MathJax>.
      </p>

      <p>
        A <strong>basic solution</strong> is constructed by setting all nonbasic variables to zero and solving
        for the basic variables:
      </p>
      <MathJax>{String.raw`
        \[
        x_N = 0,\qquad x_B = B^{-1} b.
        \]
      `}</MathJax>

      <p>
        If <MathJax inline>{String.raw `$ x_B \ge 0$`}</MathJax>, the point is feasible and is called a
        <strong>basic feasible solution (BFS)</strong>. A fundamental fact is that BFSs correspond exactly to
        vertices of the feasible polytope. So changing the basis means moving to a neighboring vertex.
      </p>

      <p>
        One subtlety: a basis can be <strong>degenerate</strong> if some basic variable is zero. Degeneracy matters
        because it can lead to pivots that do not move geometrically (no objective improvement), which is the root of cycling.
      </p>


      <h2 className="text-sm font-semibold md:text-base">5. Optimality signals: reduced costs</h2>
      <p>
        Simplex doesn’t guess a direction randomly. It uses an optimality test that comes directly from
        the KKT optimality conditions for LPs.
      </p>

      <p>
        Given a basis, we compute a dual vector <MathJax inline>{String.raw `$\lambda $`}</MathJax> from
      </p>
      <MathJax>{String.raw`
        \[
        B^T \lambda = c_B.
        \]
      `}</MathJax>

      <p>
        Then the <strong>reduced costs</strong> for nonbasic variables are
      </p>
      <MathJax>{String.raw`
        \[
        s_N = c_N - A_N^T \lambda.
        \]
      `}</MathJax>

      <p>
        Intuition: a reduced cost tells you whether increasing a currently-zero nonbasic variable would
        improve the objective while staying feasible (after compensating through the basic variables).
      </p>

      <ul className="list-disc pl-6 space-y-2">
        <li>
          If <strong>all</strong> reduced costs satisfy <MathJax inline>{String.raw `$ s_N \ge 0$`}</MathJax> (for minimization),
          then no nonbasic variable can improve the objective → the current BFS is <strong>optimal</strong>.
        </li>
        <li>
          If some component <MathJax inline>{String.raw `$s_q < 0$`}</MathJax>, then letting variable <MathJax inline>{String.raw `$x_q$`}</MathJax> increase from zero can decrease 
          the objective → <MathJax inline>{'q'}</MathJax> is a candidate <strong>entering variable</strong>.
        </li>
      </ul>


      <h2 className="text-sm font-semibold md:text-base">6. One simplex pivot step (entering/leaving variables)</h2>
      <p>
        Suppose we choose an entering index <MathJax inline>{'\(q \in N\)'}</MathJax> with negative reduced cost.
        We now ask: how far can we increase <MathJax inline>{String.raw `$x_q$`}</MathJax> while maintaining feasibility?
      </p>

      <p>
        Because we must keep <MathJax inline>{'\(Ax=b\)'}</MathJax> satisfied, increasing <MathJax inline>{String.raw `$x_q$`}</MathJax> forces 
        the basic variables to change. The direction is found by solving
      </p>
      <MathJax>{String.raw`
        \[
        B d = A_q,
        \]
      `}</MathJax>
      <p>
        which gives how the basic variables respond. The updated basic variables are
      </p>
      <MathJax>{String.raw`
        \[
        x_B^{+} = x_B - d\,x_q^{+}.
        \]
      `}</MathJax>

      <h3 className="text-sm font-semibold md:text-base">6.1 Minimum ratio test (choose the leaving variable)</h3>
      <p>
        To remain feasible we need <MathJax inline>{String.raw` $x_B^{+} \ge 0$`}</MathJax>.
        If some components of <MathJax inline>{'d'}</MathJax> are positive, they will drive
        corresponding basic variables down as <MathJax inline>{String.raw `$x_q$`}</MathJax> increases.
        The maximum step is therefore
      </p>

      <MathJax>{String.raw`
        \[
        x_q^{+} = \min_{i:\, d_i > 0}\; \frac{(x_B)_i}{d_i}.
        \]
      `}</MathJax>

      <p>
        The index achieving this minimum is the <strong>leaving variable</strong>. After the pivot, <MathJax inline>{'q'}</MathJax> enters the 
        basis and the leaving index leaves. This basis exchange is the
        algebraic version of “walking to the next vertex”.
      </p>

      <h3 className="text-sm font-semibold md:text-base">6.2 What if the ratio test has no candidate?</h3>
      <p>
        If <MathJax inline>{String.raw `$d \le 0$`}</MathJax> (componentwise), then increasing <MathJax inline>{String.raw `$x_q$`}</MathJax> never violates feasibility — 
        we can move indefinitely and keep decreasing the objective.
        In that case the LP is <strong>unbounded</strong>.
      </p>


      <h2 className="text-sm font-semibold md:text-base">7. Tableau simplex vs revised simplex</h2>
      <p>
        Many textbooks show simplex using a <strong>tableau</strong> (one big augmented matrix with row operations).
        That’s great for learning because you can see pivots directly as elementary row operations.
      </p>
      <p>
        Practical solvers usually implement the <strong>revised simplex method</strong>: instead of maintaining the full tableau,
        they maintain the basis matrix <MathJax inline>{'B'}</MathJax> and solve linear systems with <MathJax inline>{'B'}</MathJax> to compute reduced costs and pivot 
        directions. This is much more efficient for large sparse problems.
      </p>
      <h2 className="text-sm font-semibold md:text-base">8. Phase I: finding a feasible starting basis</h2>
      <p>
        Simplex needs a starting BFS. Sometimes it’s obvious (e.g., slack variables give an identity matrix),
        but in general it isn’t. The standard solution is the <strong>two-phase method</strong>.
      </p>

      <p>
        In <strong>Phase I</strong> we introduce <strong>artificial variables</strong> that make feasibility easy, and minimize the
        sum of these artificial variables. Conceptually, the artificial variables measure “how much we violate”
        the original constraints.
      </p>

      <p>
        If the optimal Phase-I objective is zero, we found a feasible basis for the original constraints.
        If it is positive, the original LP is <strong>infeasible</strong>.
      </p>


      <h2 className="text-sm font-semibold md:text-base">9. Phase II: optimize the original objective</h2>
      <p>
        Once a feasible basis is available, we restore the original objective and run simplex pivots to improve it.
        Artificial variables are kept only as long as they remain in the basis (typically with value zero),
        and are removed when possible to avoid wasting iterations.
      </p>


      <h2 className="text-sm font-semibold md:text-base">10. Degeneracy, cycling, and pivot rules</h2>
      <p>
        If a BFS is <strong>degenerate</strong>, a pivot can result in <MathJax inline>{String.raw `$x_q^{+}=0$`}</MathJax>, meaning the algorithm changes the basis but does 
        not move to a different point in the polytope.
        This can lead to <strong>cycling</strong> (revisiting the same bases).
      </p>

      <p>
        Practical simplex implementations avoid this with pivot rules and safeguards. Two common ideas:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Dantzig rule:</strong> choose the most negative reduced cost as entering variable (fast in practice, but can cycle on contrived examples).
        </li>
        <li>
          <strong>Bland’s rule:</strong> choose the smallest index that improves (prevents cycling, but may be slower).
        </li>
      </ul>

      <h2 className="text-sm font-semibold md:text-base">11. Termination conditions (what the algorithm can conclude)</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Optimal:</strong> all reduced costs satisfy the optimality sign condition (for minimization: <MathJax inline>{String.raw `$ s_N \ge 0 $`}</MathJax> ).
        </li>
        <li>
          <strong>Unbounded:</strong> an improving entering variable exists, but the direction keeps feasibility for arbitrarily large steps (ratio test fails).
        </li>
        <li>
          <strong>Infeasible:</strong> Phase I cannot drive the artificial objective to zero.
        </li>
      </ul>

      <h2 className="text-sm font-semibold md:text-base">12. Numerical stability</h2>

      <p>
        Over many updates, numerical errors and fill-in can accumulate, so implementations periodically
        recompute a fresh factorization. This is one reason why “simple” simplex can be extremely fast
        in practice but still needs careful numerical engineering.
      </p>

    </div>
  );
}
